import Axios from "../../../lib/Axios";

/**
 * Reported Broadcast API Helper
 * 
 * API functions for reported broadcast page
 */

/**
 * Get reported broadcast list
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.keyword - Keyword to search (username)
 * @param {boolean|string} filters.is_suspended - Suspension status (true, false, or undefined)
 * @returns {Promise} API response with broadcast_reports array and pagination
 */
export const getReportedBroadcastList = async (filters = {}) => {
  const {
    limit = 50,
    page = 1,
    keyword,
    is_suspended,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (keyword) params.keyword = keyword;
  if (is_suspended !== undefined && is_suspended !== "") {
    // Convert string "true"/"false" to boolean, or keep boolean as is
    if (is_suspended === "true" || is_suspended === true) {
      params.is_suspended = true;
    } else if (is_suspended === "false" || is_suspended === false) {
      params.is_suspended = false;
    }
  }

  console.log("API Params:", params);

  // GET /admin/broadcast-reports
  const result = await Axios.get("admin/broadcast-reports", { params });
  const broadcastReports = result?.data?.result?.broadcast_reports || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: broadcastReports,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get broadcasts by username
 * @param {Object} filters - Filter parameters
 * @param {string} filters.userId - User ID (required)
 * @param {string} filters.broadcast_id - Broadcast ID filter
 * @param {string} filters.status - Status filter (active, expired, pending, rejected)
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {boolean|string} filters.isReported - Is reported filter
 * @param {string} filters.titleKeyword - Search by title keyword
 * @param {string} filters.descriptionKeyword - Search by description keyword
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response with broadcasts array and pagination
 */
export const getBroadcastsByUsername = async (filters = {}) => {
  const {
    userId,
    broadcast_id,
    status,
    dateRange,
    startDate,
    endDate,
    isReported,
    titleKeyword,
    descriptionKeyword,
    limit = 50,
    page = 1,
  } = filters;

  if (!userId) {
    throw new Error("userId is required");
  }

  // Build query parameters object
  const params = {
    userId: userId,
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (broadcast_id) params.broadcast_id = broadcast_id;
  if (status) params.status = status;
  if (dateRange && !startDate && !endDate) {
    params.dateRange = dateRange;
  }
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (isReported !== undefined && isReported !== "") {
    if (isReported === "true" || isReported === true) {
      params.isReported = true;
    } else if (isReported === "false" || isReported === false) {
      params.isReported = false;
    }
  }
  if (titleKeyword) params.titleKeyword = titleKeyword;
  if (descriptionKeyword) params.descriptionKeyword = descriptionKeyword;

  console.log("Broadcasts by Username API Params:", params);

  // GET /admin/broadcasts
  const result = await Axios.get("admin/broadcasts", { params });
  const broadcasts = result?.data?.result?.broadcasts || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    broadcasts: broadcasts,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Expire a broadcast
 * @param {Object} data - Expire data
 * @param {string} data.broadcast_id - The broadcast ID to expire
 * @param {string} data.adminExpirationText - Admin expiration reason
 * @returns {Promise} API response
 */
export const expireBroadcast = async (data) => {
  const { broadcast_id, adminExpirationText } = data;
  
  if (!broadcast_id) {
    throw new Error("broadcast_id is required");
  }
  if (!adminExpirationText) {
    throw new Error("adminExpirationText is required");
  }

  // POST /cms/broadcast/set-expire
  const result = await Axios.post("cms/broadcast/set-expire", {
    broadcast_id,
    adminExpirationText,
  });
  
  return result;
};

